import { useEffect, useState } from "react";
import { BookOpen, ExternalLink, Target, Users, Mail, Megaphone } from "lucide-react";
import api from "@/lib/api";
import HeroImage from "@/components/HeroImage";

const OFFICIAL_URL = "https://revue-unisic.org";

const FEATURED_DEFAULT = {
  volume: "Volume 22",
  issue: "Numéro 1",
  year: "2025",
  articles_count: "15 articles",
  publication_date: "Juin 2025",
  title: "Information — Relation — Communication",
  theme: "Cahiers Congolais de Communication",
  description:
    "Le volume XXII n°1 des Cahiers Congolais de Communication démontre l'incontournabilité de l'interdisciplinarité en Sciences de l'Information et de la Communication. Les contributeurs mettent en lumière la dimension communicationnelle des phénomènes sociaux étudiés.",
  cover_image: "https://revue-unisic.org/api/uploads/951e0efb-78fa-425b-98ee-0d17b8c43c23",
  read_url: "https://revue-unisic.org/cahiers-congolais-de-communication/vol22/num1",
};

const CALL_DEFAULT = {
  eyebrow: "Appel à communication en vedette",
  title: "La télévision congolaise (RDC) à l'ère des dynamiques globales et numériques",
  subtitle: "60 ans d'évolutions en perspective comparative",
  deadline: "25 octobre 2026",
  url: "https://revue-unisic.org/appel-a-communication",
};

export default function Revues() {
  const [banner, setBanner] = useState("");
  const [featured, setFeatured] = useState(FEATURED_DEFAULT);
  const [call, setCall] = useState(CALL_DEFAULT);

  useEffect(() => {
    api
      .get("/content/platform.revues")
      .then((r) => {
        const d = r.data || {};
        if (d.banner_image) setBanner(d.banner_image);
        if (d.featured) setFeatured({ ...FEATURED_DEFAULT, ...d.featured });
        if (d.call) setCall({ ...CALL_DEFAULT, ...d.call });
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="revues-page">
      <section className="relative min-h-[70vh] overflow-hidden">
        <HeroImage src={banner} />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center min-h-[70vh]">
          <div className="lg:col-span-7 text-white">
            <div className="eyebrow" style={{ color: "#E5C158" }}>
              Revue scientifique
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Les Cahiers Congolais de{" "}
              <span className="text-unisic-gold">Communication</span>
            </h1>
            <p className="mt-6 text-white/85 text-lg max-w-2xl">
              Revue scientifique interdisciplinaire du Centre d'Études en Communication
              (CECOM) — publications originales en sciences de l'information et de la
              communication.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={OFFICIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="btn-revue-official"
              >
                Accéder au site officiel de la revue <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href={featured.read_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-white"
                data-testid="btn-revue-current"
              >
                Lire le numéro en cours <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-3 text-xs text-white/60">
              La revue est hébergée sur <span className="text-unisic-gold">{OFFICIAL_URL.replace("https://", "")}</span>
            </div>
          </div>

          {/* Numéro en vedette — dupliqué de la hero du site officiel */}
          <div className="lg:col-span-5">
            <div className="relative" data-testid="featured-issue">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-unisic-gold" />
              <a
                href={featured.read_url}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block bg-white/95 backdrop-blur border border-white/40 rounded-sm overflow-hidden group hover:shadow-2xl transition-shadow"
              >
                {featured.cover_image && (
                  <img
                    src={featured.cover_image}
                    alt={featured.title}
                    loading="lazy"
                    className="w-full h-64 object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                )}
                <div className="p-6">
                  <div className="inline-flex items-center gap-2">
                    <span className="text-[10px] font-mono tracking-widest bg-unisic-red text-white px-2 py-1 rounded-sm">
                      NOUVEAU
                    </span>
                    <span className="text-[11px] uppercase tracking-widest text-unisic-blue font-semibold">
                      {featured.volume} · {featured.issue}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display font-bold text-unisic-blue text-xl leading-snug">
                    {featured.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 line-clamp-4">
                    {featured.description}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-slate-500">
                    <span>{featured.year}</span>
                    <span>·</span>
                    <span>{featured.articles_count}</span>
                    <span>·</span>
                    <span>{featured.publication_date}</span>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-unisic-blue group-hover:text-unisic-gold transition-colors">
                    Lire le numéro <ExternalLink className="w-4 h-4" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Appel à communication en vedette */}
      {call.url && (
        <section
          className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16"
          data-testid="revue-featured-call"
        >
          <div className="paper-card p-6 md:p-8 border-l-4 border-unisic-red flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-unisic-red/10 text-unisic-red flex-shrink-0">
                <Megaphone className="w-5 h-5" />
              </div>
              <div>
                <div className="eyebrow eyebrow-dark">{call.eyebrow}</div>
                <h3 className="mt-2 text-xl font-bold text-unisic-blue leading-snug">
                  {call.title}
                </h3>
                {call.subtitle && (
                  <p className="text-sm text-slate-600 mt-1">{call.subtitle}</p>
                )}
                {call.deadline && (
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-unisic-red font-semibold">
                    Date limite : {call.deadline}
                  </div>
                )}
              </div>
            </div>
            <a
              href={call.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex-shrink-0"
            >
              Consulter l'appel <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-8">
        <div>
          <Target className="w-6 h-6 text-unisic-gold" />
          <h3 className="mt-3 font-display font-semibold text-unisic-blue text-lg">
            Objectifs
          </h3>
          <span className="divider-gold mt-2" />
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            Diffuser les résultats de la recherche en communication, encourager les
            jeunes chercheurs et renforcer les liens entre les universités africaines.
          </p>
        </div>
        <div>
          <Users className="w-6 h-6 text-unisic-gold" />
          <h3 className="mt-3 font-display font-semibold text-unisic-blue text-lg">
            Comité éditorial
          </h3>
          <span className="divider-gold mt-2" />
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            Un comité international composé d'enseignants-chercheurs de l'UNISIC et
            d'universités partenaires évalue chaque soumission en double aveugle.
          </p>
        </div>
        <div>
          <BookOpen className="w-6 h-6 text-unisic-gold" />
          <h3 className="mt-3 font-display font-semibold text-unisic-blue text-lg">
            Soumettre & Consulter
          </h3>
          <span className="divider-gold mt-2" />
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            L'ensemble des numéros et le formulaire de soumission sont accessibles
            directement sur{" "}
            <a
              href={OFFICIAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline"
            >
              revue-unisic.org
            </a>
            .
          </p>
          <a
            href={`${OFFICIAL_URL}/archives`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-unisic-blue hover:text-unisic-gold"
          >
            <Mail className="w-4 h-4" /> Voir les archives
          </a>
        </div>
      </section>
    </div>
  );
}
